/**
 * Inclusive bounds for a significance level α. Mirrors Accord.NET
 * `HypothesisTest.Size` (`TwoProportionZTest` / `TwoSampleTTest`), which throws
 * `ArgumentOutOfRangeException` outside [0, 1].
 */
export const MIN_SIGNIFICANCE_LEVEL = 0;
export const MAX_SIGNIFICANCE_LEVEL = 1;

export const isSignificanceLevelInRange = (value: number): boolean =>
  value >= MIN_SIGNIFICANCE_LEVEL && value <= MAX_SIGNIFICANCE_LEVEL;

/**
 * Default significance level (α) for results analyses. Mirrors the SurveyApi
 * default and is used by scorecard, crosstabs, penalty and alienation
 * analyses when the user has not explicitly picked a confidence level.
 *
 * Confidence % is `100 * (1 - α)` — derive it with `toConfidenceLevelPercent`
 * rather than storing a second literal that can drift from this value.
 */
export const DEFAULT_SIGNIFICANCE_LEVEL = 0.05;

/** Converts a significance level α to the confidence percentage researchers see (95 for 0.05). */
export const toConfidenceLevelPercent = (significanceLevel: number): number =>
  100 - significanceLevel * 100;

export const DEFAULT_CONFIDENCE_LEVEL_PERCENT = toConfidenceLevelPercent(
  DEFAULT_SIGNIFICANCE_LEVEL,
);

export const DEFAULT_CONFIDENCE_LEVEL_LABEL = `${DEFAULT_CONFIDENCE_LEVEL_PERCENT}%`;

/**
 * Minimum sample size before crosstab / scorecard significance testing runs.
 * Mirrors SurveyApi `CrossTabStatsWorker.SignificanceMinResponseCount`. Also the
 * floor for Insights Chat `largeEnoughToCompare` / `largeEnoughToSummarize` — those
 * flags are a base-size check, not a significance test
 * (see `packages/ai/src/tools/insights-chat/SIGNIFICANCE.md`).
 */
export const SIGNIFICANCE_MIN_RESPONSE_COUNT = 15;

export const isBaseLargeEnough = (responseCount: number): boolean =>
  responseCount >= SIGNIFICANCE_MIN_RESPONSE_COUNT;
